import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import supabaseService from '../services/supabaseService';
// ✅ تصحيح الاستيراد
import { FiArrowRight, FiRefreshCw, FiArrowDownCircle, FiArrowUpCircle, FiClock } from 'react-icons/fi';

export default function WalletTransactionsScreen() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadTransactions();
  }, [user]);

  const loadTransactions = async () => {
    if (!user) return;
    setLoading(true);
    setError(null);
    try {
      const data = await supabaseService.getWalletTransactions(user.id);
      setTransactions(data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const totalCredit = transactions.filter(t => t.type === 'credit').reduce((sum, t) => sum + t.amount, 0);
  const totalDebit = transactions.filter(t => t.type === 'debit').reduce((sum, t) => sum + t.amount, 0);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (days === 0) return 'اليوم';
    if (days === 1) return 'أمس';
    if (days < 7) return `منذ ${days} أيام`;
    return date.toLocaleDateString('ar-DZ');
  };

  return (
    <div className="h-screen w-full bg-gray-50 font-sans flex flex-col overflow-hidden" dir="rtl">

      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 lg:px-10 py-3 flex items-center justify-between shadow-sm shrink-0">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors">
            <FiArrowRight className="text-gray-700" />
          </button>
          <h1 className="text-lg font-bold text-gray-900">سجل المعاملات</h1>
        </div>
        <button onClick={loadTransactions} className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors">
          <FiRefreshCw className={`text-gray-600 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Main content — fills remaining height, no page scroll */}
      <div className="flex-1 min-h-0 px-6 lg:px-10 py-5 lg:py-8">
        <div className="h-full max-w-4xl mx-auto flex flex-col gap-5 min-h-0">

          {/* ملخص الواردات / الصادرات */}
          <div className="grid grid-cols-2 gap-4 shrink-0">
            <div className="bg-green-50 border border-green-200 p-4 lg:p-5 rounded-2xl">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center text-green-600">
                  <FiArrowDownCircle />
                </div>
                <span className="text-xs text-green-700 font-bold">إجمالي الواردات</span>
              </div>
              <p className="text-xl lg:text-2xl font-extrabold text-green-800">+{totalCredit.toFixed(2)} دج</p>
            </div>
            <div className="bg-red-50 border border-red-200 p-4 lg:p-5 rounded-2xl">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center text-red-600">
                  <FiArrowUpCircle />
                </div>
                <span className="text-xs text-red-700 font-bold">إجمالي الصادرات</span>
              </div>
              <p className="text-xl lg:text-2xl font-extrabold text-red-800">-{totalDebit.toFixed(2)} دج</p>
            </div>
          </div>

          {/* قائمة المعاملات — تملأ ما تبقى من الارتفاع، تمرير داخلي فقط عند الحاجة */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 flex-1 min-h-0 flex flex-col overflow-hidden">
            {loading ? (
              <div className="flex-1 flex items-center justify-center text-gray-400">جاري التحميل...</div>
            ) : error ? (
              <div className="flex-1 flex items-center justify-center">
                <div className="bg-red-50 text-red-600 p-4 rounded-xl text-center">{error}</div>
              </div>
            ) : transactions.length === 0 ? (
              <div className="flex-1 flex flex-col items-center justify-center text-gray-400">
                <FiClock className="mb-2" size={32} />
                <p>لا توجد معاملات بعد</p>
              </div>
            ) : (
              <div className="flex-1 min-h-0 overflow-y-auto divide-y divide-gray-100">
                {transactions.map((tx) => (
                  <div key={tx.id} className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${tx.type === 'credit' ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'}`}>
                        {tx.type === 'credit' ? <FiArrowDownCircle size={18} /> : <FiArrowUpCircle size={18} />}
                      </div>
                      <div>
                        <p className="font-bold text-gray-800">{tx.type === 'credit' ? 'وارد' : 'صادر'}</p>
                        <p className="text-xs text-gray-400">{tx.note || 'معاملة'}</p>
                      </div>
                    </div>
                    <div className="text-left">
                      <p className={`font-bold ${tx.type === 'credit' ? 'text-green-600' : 'text-red-600'}`}>
                        {tx.type === 'credit' ? '+' : '-'}{tx.amount.toFixed(0)} دج
                      </p>
                      <p className="text-[10px] text-gray-400">
                        {formatDate(tx.created_at)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
}